import { useCallback } from 'react';

import { MappingCollections } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { capitalizeAndSplitOnUpperCase } from '@/utils';

/** Maps an inventory provider id to its measurement class name when applicable. */
const resolveProviderLookupKey = (providerOrClassName: string): string =>
  MappingCollections.inventoryProviderClassPair[providerOrClassName] ?? providerOrClassName;

/**
 * A hook that provides a function to get a user-friendly name for a given provider.
 * Accepts either an `InventoryProvider` id or a measurement class name.
 * It uses internationalization (i18n) to return the translated name if available, and falls back to a default value if not.
 * The default value can be either a mapped friendly name from `MappingCollections` or a capitalized version of the provider string.
 * @param capitalizeDefaultResult - When true, unknown keys fall back to `capitalizeAndSplitOnUpperCase` (automation/dropdowns). When false, returns the raw key (chart series).
 */
export const useGetFriendlyProviderName = (capitalizeDefaultResult?: boolean | undefined) => {
  const { t } = useTranslation(['chartConfig']);

  const getDefaultValue = useCallback(
    (providerOrClassName: string) => {
      const className = resolveProviderLookupKey(providerOrClassName);
      const mappedValue = MappingCollections.classNameToFriendlyProviderName[className];
      if (mappedValue) {
        return mappedValue;
      }
      return capitalizeDefaultResult ? capitalizeAndSplitOnUpperCase(className) : className;
    },
    [capitalizeDefaultResult]
  );

  const getFriendlyProviderName = useCallback(
    (providerOrClassName: string): string => {
      const className = resolveProviderLookupKey(providerOrClassName);
      return t(`chartConfig:providerFriendlyName.${className}`, {
        defaultValue: getDefaultValue(providerOrClassName),
      });
    },
    [getDefaultValue, t]
  );

  return { getFriendlyProviderName };
};
